import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';

import { SettingsService } from 'app/modules/settings/settings.service';
import { AppIcons } from 'app/modules/shared/app.icons';
import { AppSettings } from 'app/modules/shared/app.settings';
import { AutoFocusDirective } from 'app/modules/shared/directives/auto-focus.directive';
import { Country } from 'app/modules/shared/models/country';
import { Language } from 'app/modules/shared/models/language';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { TabViewModule } from 'primeng/tabview';
import { passwordMatchValidator } from '../../../../../modules/shared/utility-functions/passwordMatch';
import { LoginModal } from 'app/modules/settings/models/login.models';
import { TrimSpacesProfileDirective } from 'app/modules/shared/directives/trim-spaces-for-profile.directive';
import { PasswordModule } from 'primeng/password';
import { AttributeData } from '../../../../shared/models/attribute.models';
import { EntityList } from '../../../../shared/models/entity.models';
import { CommonModule } from '@angular/common';
import { EncryptionService } from 'app/shared/services/encryption.service';

@Component({
  selector: 'app-settings-login-and-password',
  standalone: true,
  imports: [TranslateModule, ButtonModule, ToastModule, InputTextModule, CheckboxModule, ReactiveFormsModule, FormsModule, TabViewModule, DropdownModule, TrimSpacesProfileDirective, PasswordModule, CommonModule],
  templateUrl: './settings-login-and-password.component.html',
  styleUrl: './settings-login-and-password.component.scss',
  providers: [AutoFocusDirective]
})
export class SettingsLoginAndPasswordComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput!: ElementRef;
  miIcons = AppIcons;
  loginForm: FormGroup;
  changePasswordForm: FormGroup;
  updateLabel = this.cs.getLabel('lbl_update');
  changePasswordLabel = this.cs.getLabel('lbl_change_password');
  editLabel: string = this.cs.getLabel('settings.edit');
  deleteLabel: string = this.cs.getLabel('settings.delete');
  deleteHeaderMsg: string = this.cs.getLabel('settings.events.delete_header');
  deleteMsg: string = this.cs.getLabel('settings.events.delete_msg');
  userDetails: any;
  userId: number;
  countries: any[] | undefined;
  selectedCountry: any;
  userName: string;
  language: Language;
  timezoneList;
  selectedTimezone: any;
  country: Country;
  data: AttributeData;
  userProfileAttributeData;
  attributeIdForUserProfile;
  attributeValues;
  minLength;
  initialLoginFormValues: any;
  passwordMinLengthValidator = AppSettings.PASSWORD_PATTERN;
  requireAtLeastOne;
  passwordRequirementsMessage: string;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.BOTH,
    forTenantCode: this.configService.getLoggedInTenantCode(),
  };
  filterIcon = 'pi pi-search';
  errorMessage;
  passwordType: boolean;
  inputType = 'password';
  showPassword = false;
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(private cs: CommonBindingDataService, private formBuilder: FormBuilder,
    private configService: ConfigService, private entityService: EntityService, private settingsService: SettingsService,
    private messageService: MessageService, private confirmationService: ConfirmationService,
    private encryptionService: EncryptionService) {
  }

  ngOnInit() {
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.onShowLogin();
    this.formValidation();
    this.getUserId();
    this.initializePasswordRequirements();
  }

  toggleNewPassword() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleCurrentPassword() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  formValidation() {
    this.loginForm = this.formBuilder.group({
      userName: ['', [Validators.required, Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.pattern(AppSettings.EMAIL_PATTERN)]],
      timezones: [''],
      emailUpdates: [false],
    });

    this.changePasswordForm = this.formBuilder.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required,Validators.maxLength(20) ,Validators.pattern(AppSettings.PASSWORD_PATTERN_NO_WS)]],
      confirmPassword: ['', [Validators.required, Validators.maxLength(20)]],
    }, { validator: passwordMatchValidator });
  }

  checkPasswordMatch() {
    const password = this.changePasswordForm.controls.newPassword.value;
    const confirmPassword = this.changePasswordForm.controls.confirmPassword.value;

    if (password && confirmPassword && password !== confirmPassword) {
      this.errorMessage = this.cs.getLabel('lbl_please_match_the_password_with_new_password');
    } else {
      this.errorMessage = '';
    }
  }

  onShowLogin() {
    if (this.nameInput) {
      this.nameInput?.nativeElement?.focus();
    }
  }

  getUserId() {
    const profileData = JSON.parse(localStorage.getItem(AppSettings.PROFILE_DATA));
    this.userName = profileData.username;
    this.userId = profileData.userId;
    if (this.userId) {
      this.getUserDetails(this.userId);
    }
  }

  getUserDetails(userId) {
    this.entityService.getUserDetails(userId, this.configService.getForTenantCode()).subscribe((result: LoginModal) => {
      this.userDetails = result;
      this.initialLoginFormValues = {
        userName: this.userDetails.username,
        email: this.userDetails.email
      };
      this.loginForm.controls.userName.setValue(this.userDetails.username);
      this.loginForm.controls.email.setValue(this.userDetails.email);
    })
  }

  getAttributeId(attributeCode) {
    const fields = this.userProfileAttributeData.tabs[0].groups[0].fields;
    const field = fields.find(field => field.attributeCode === attributeCode);
    return field ? field.attributeId : null;
  }

  initializePasswordRequirements() {
    this.attributeValues = {
      min_length: 8,
      require_at_least_one: [
        'Uppercase and Lower case letter',
        'Numeric character (0-9)',
        'Special character (!, @, #, etc.)'
      ]
    };
    this.minLength = this.attributeValues.min_length;
    const requireAtLeastOne = this.attributeValues.require_at_least_one;
    const passwordRequirements = [];
    if (requireAtLeastOne) {
      if (requireAtLeastOne.includes('Uppercase and Lower case letter')) {
        passwordRequirements.push(this.cs.getLabel('lbl_must_contain_uppercase_lowercase'));
      }
      if (requireAtLeastOne.includes('Numeric character (0-9)')) {
        passwordRequirements.push(this.cs.getLabel('lbl_must_contain_digits'));
      }
      if (requireAtLeastOne.includes('Special character (!, @, #, etc.)')) {
        passwordRequirements.push(this.cs.getLabel('lbl_must_contain_special_character'));
      }
    }
    this.passwordRequirementsMessage = passwordRequirements.join(', ');
    const baseConditions = [];
    if (requireAtLeastOne.includes('Uppercase and Lower case letter')) {
      baseConditions.push('(?=.*[A-Z])');
      baseConditions.push('(?=.*[a-z])');
    }

    if (requireAtLeastOne.includes('Numeric character (0-9)')) {
      baseConditions.push('(?=.*\\d)');
    }

    if (requireAtLeastOne.includes('Special character (!, @, #, etc.)')) {
      baseConditions.push('(?=.*[!@#$%^&*()_+\\-=[\\]{};:"\\,.<>/?|`~])');
    }

    const lengthCondition = `.{${this.minLength},}$`;
    const updatedPattern = baseConditions.join('') + lengthCondition;
    this.passwordMinLengthValidator = new RegExp(updatedPattern);
    this.changePasswordForm.controls.newPassword.setValidators([Validators.required, Validators.maxLength(20),Validators.pattern(this.passwordMinLengthValidator)]);
    this.changePasswordForm.controls.newPassword.updateValueAndValidity();
  }

  patchTimezone(timezone: string) {
    const timezoneObj = this.timezoneList?.find(tz => tz.timezone === timezone);
    if (timezoneObj) {
      this.loginForm.get('timezones').setValue(timezoneObj);
    }
  }

  onCancel() {
    this.confirmationService.confirm({
      header: this.cs.getLabel('settings.notification.lbl_confirmation'),
      message: this.cs.getLabel('settings.notification.confirm_discard_header'),
      accept: () => {
        this.clearModifiedFields();
        this.onShowLogin();
      },
      reject: () => {
      }
    });
  }

  clearModifiedFields() {
    if (this.loginForm.controls.userName.dirty) {
      this.loginForm.controls.userName.setValue(this.initialLoginFormValues.userName);
    }

    if (this.loginForm.controls.email.dirty) {
      this.loginForm.controls.email.setValue(this.initialLoginFormValues.email);
    }
  }

  onCancelChangePwd() {
    this.changePasswordForm.reset();
    this.errorMessage = ''; 
  }

  onAddLoginInfo() {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    if (this.loginForm.valid) {
      const reqBody = {
        forTenantCode: this.configService.getForTenantCode(),
        countryCode: country[0].countryCode,
        languageCode: this.language[0].langCode,
        userName: this.loginForm.value.userName,
        email: this.loginForm.value.email,
        timezone: this.loginForm.value.timezones.timezone ? this.loginForm.value.timezones.timezone : '',
        emailUpdates: this.loginForm.value.emailUpdates,
      }
      this.settingsService.updateLoginInformationById(this.userId, reqBody).subscribe((res: any) => {
        this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel(res.message) });
      },
        () => {
          this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel('lbl_invalid_data') });
        });
    }
  }

  onChangePassword() {
    this.changePasswordForm.controls.currentPassword.markAsTouched();
    this.changePasswordForm.controls.currentPassword.updateValueAndValidity();

    this.changePasswordForm.controls.newPassword.updateValueAndValidity();
    this.changePasswordForm.controls.confirmPassword.updateValueAndValidity();

    const newPassword = this.changePasswordForm.value.newPassword;
    const confirmPassword = this.changePasswordForm.value.confirmPassword;

    if (newPassword !== confirmPassword) {
      this.messageService.add({
        key: 'tst',
        severity: 'error',
        summary: 'Error',
        detail: this.cs.getLabel('settings.lbl_error_password')
      });
      return;
    }

    if (this.changePasswordForm.valid) {
      try {
        // Encrypt both current and new passwords before sending to API
        const encryptedCurrentPassword = this.encryptionService.encryptPassword(this.changePasswordForm.value.currentPassword);
        const encryptedNewPassword = this.encryptionService.encryptPassword(this.changePasswordForm.value.newPassword);

        const reqBody = {
          forTenantCode: this.configService.getForTenantCode(),
          currentPassword: encryptedCurrentPassword,
          newPassword: encryptedNewPassword,
        }
        this.settingsService.updatePasswordById(this.userId, reqBody).subscribe((res: any) => {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel(res.message) });
          this.changePasswordForm.reset();
        },
          error => {
            this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].message) });
          });
      } catch (error) {
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to process password change. Please try again.'
        });
      }
    }
  }

}
